import {
  Meeting,
  ApprovalLogEntry,
  Committee,
  CommitteeMember,
  GovernanceFramework,
  GovernanceSnapshot,
  TermsOfReference,
  VersionHistoryEntry,
  VotingDecision,
} from "@/types/governance";

const now = new Date();
const plusDays = (days: number) => new Date(now.getTime() + days * 24 * 60 * 60 * 1000).toISOString();
const minusDays = (days: number) => new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString();

export const governanceMockMembers: CommitteeMember[] = [
  {
    id: "m-1",
    name: "Faisal Chishti",
    email: "faisal@manageaze.com",
    designation: "Board Chair",
    governanceLevel: "L1",
    roles: ["chairperson", "approver"],
  },
  {
    id: "m-2",
    name: "Saad Hasnain",
    email: "saad@manageaze.com",
    designation: "Head of Technology",
    governanceLevel: "L2",
    roles: ["committee_owner", "member"],
  },
  {
    id: "m-3",
    name: "Zoya Sifaat",
    email: "ayesha@manageaze.com",
    designation: "Chief Risk Officer",
    governanceLevel: "L2",
    roles: ["approver", "member"],
  },
  {
    id: "m-4",
    name: "Meeran Fehmi",
    email: "omar@manageaze.com",
    designation: "Finance Director",
    governanceLevel: "L2",
    roles: ["approver", "reviewer", "member"],
  },
  {
    id: "m-5",
    name: "Saad Vakil",
    email: "sana@manageaze.com",
    designation: "Governance Analyst",
    governanceLevel: "L3",
    roles: ["reviewer", "member"],
  },
  {
    id: "m-6",
    name: "Humayun Chishti",
    email: "bilal@manageaze.com",
    designation: "CFO (Attendee)",
    governanceLevel: "L2",
    roles: ["attendee"],
  },
];

export const governanceMockFrameworks: GovernanceFramework[] = [
  {
    id: "f-1",
    name: "Enterprise Governance Framework 2026",
    description: "Primary governance structure for board and executive committees.",
    status: "draft",
    chairpersonId: "m-1",
    createdBy: "m-2",
    createdAt: minusDays(30),
    updatedAt: minusDays(1),
  },
];

export const governanceMockCommittees: Committee[] = [
  {
    id: "c-1",
    frameworkId: "f-1",
    name: "Board of Directors",
    description: "Top-level governing body responsible for strategic oversight.",
    level: 1,
    parentId: null,
    type: "standing",
    ownerId: "m-2",
    chairpersonId: "m-1",
    memberIds: ["m-1", "m-2", "m-3", "m-4", "m-6"],
    minimumMembersRequired: 5,
    meetingFrequency: "quarterly",
    reportingTo: "Shareholders",
    approvalAuthority: "Board Chair",
    torId: "tor-1",
  },
  {
    id: "c-2",
    frameworkId: "f-1",
    name: "Audit Committee",
    description: "Oversees financial reporting, controls, and audit outcomes.",
    level: 2,
    parentId: "c-1",
    type: "standing",
    ownerId: "m-4",
    chairpersonId: "m-3",
    memberIds: ["m-3", "m-4", "m-5", "m-6"],
    minimumMembersRequired: 3,
    meetingFrequency: "monthly",
    reportingTo: "Board of Directors",
    approvalAuthority: "Board Chair",
    torId: "tor-2",
  },
  {
    id: "c-3",
    frameworkId: "f-1",
    name: "Risk & Compliance Committee",
    description: "Drives enterprise risk governance and compliance assurance.",
    level: 2,
    parentId: "c-1",
    type: "standing",
    ownerId: "m-3",
    chairpersonId: "m-1",
    memberIds: ["m-1", "m-3", "m-5"],
    minimumMembersRequired: 3,
    meetingFrequency: "monthly",
    reportingTo: "Board of Directors",
    approvalAuthority: "Board Chair",
    torId: "tor-3",
  },
  {
    id: "c-4",
    frameworkId: "f-1",
    name: "Technology Steering Committee",
    description: "Owns technology oversight, architecture, and delivery governance.",
    level: 2,
    parentId: "c-1",
    type: "standing",
    ownerId: null,
    chairpersonId: "m-2",
    memberIds: ["m-2", "m-5"],
    minimumMembersRequired: 3,
    meetingFrequency: "monthly",
    reportingTo: "Board of Directors",
    approvalAuthority: "Board Chair",
    torId: null,
  },
  {
    id: "c-5",
    frameworkId: "f-1",
    name: "Cybersecurity Task Force",
    description: "Executes board-directed cybersecurity readiness initiatives.",
    level: 3,
    parentId: "c-3",
    type: "ad_hoc",
    ownerId: "m-2",
    chairpersonId: "m-3",
    memberIds: ["m-2", "m-3", "m-5"],
    minimumMembersRequired: 3,
    meetingFrequency: "bi_weekly",
    reportingTo: "Risk & Compliance Committee",
    approvalAuthority: "Chief Risk Officer",
    torId: "tor-4",
  },
];

const tor1VersionHistory: VersionHistoryEntry[] = [
  {
    id: "vh-1",
    torId: "tor-1",
    version: "v1.0",
    date: minusDays(20),
    updatedBy: "m-2",
    comments: "Initial draft completed by committee owner.",
  },
  {
    id: "vh-2",
    torId: "tor-1",
    version: "v1.1",
    date: minusDays(12),
    updatedBy: "m-2",
    comments: "Added meeting frequency and approval workflow detail.",
  },
];

const tor1ApprovalLog: ApprovalLogEntry[] = [
  {
    id: "al-1",
    torId: "tor-1",
    userId: "m-1",
    action: "approved",
    comment: "Approved from governance strategy standpoint.",
    createdAt: minusDays(9),
  },
  {
    id: "al-2",
    torId: "tor-1",
    userId: "m-3",
    action: "approved",
    comment: "No additional comments.",
    createdAt: minusDays(8),
  },
];

export const governanceMockTerms: TermsOfReference[] = [
  {
    id: "tor-1",
    frameworkId: "f-1",
    committeeId: "c-1",
    committeeOwnerId: "m-2",
    chairpersonId: "m-1",
    approverIds: ["m-1", "m-3", "m-4"],
    reviewerIds: ["m-5"],
    memberIds: ["m-1", "m-2", "m-3", "m-4", "m-6"],
    minimumMembersRequired: 5,
    reportingWorkflow: "Board Secretary collates committee updates and publishes to executive portal.",
    approvalWorkflow: "Chairperson + two approvers required to finalize.",
    version: "v1.1",
    status: "submitted",
    meetingFrequency: "quarterly",
    purpose:
      "<p>The Board of Directors provides strategic oversight and governance direction.</p>",
    rolesAndResponsibilities:
      "<ul><li>Approve strategic plans and annual budgets</li><li>Oversee enterprise risk</li><li>Review governance maturity quarterly</li></ul>",
    agendaContent:
      "<h3>Agenda</h3><ul><li>Previous meeting approvals</li><li>Risk and audit updates</li><li>Technology and innovation review</li></ul>",
    documentOwner: "Saad Hasnain",
    contactEmail: "saad@manageaze.com",
    approvalAuthority: "Board Chair",
    approvalDate: null,
    nextReviewDate: plusDays(180),
    versionHistory: tor1VersionHistory,
    approvalLog: tor1ApprovalLog,
    createdAt: minusDays(20),
    updatedAt: minusDays(8),
  },
  {
    id: "tor-2",
    frameworkId: "f-1",
    committeeId: "c-2",
    committeeOwnerId: "m-4",
    chairpersonId: "m-3",
    approverIds: ["m-1", "m-3"],
    reviewerIds: ["m-5"],
    memberIds: ["m-3", "m-4", "m-5", "m-6"],
    minimumMembersRequired: 3,
    reportingWorkflow: "Monthly control report submitted to Board of Directors.",
    approvalWorkflow: "Chairperson and board approver sign-off required.",
    version: "v1.0",
    status: "approved",
    meetingFrequency: "monthly",
    purpose: "<p>Audit Committee ensures financial reporting and controls integrity.</p>",
    rolesAndResponsibilities:
      "<ul><li>Review quarterly financials</li><li>Track external audit findings</li></ul>",
    agendaContent:
      "<h3>Agenda</h3><ul><li>Internal controls status</li><li>Audit action tracker</li></ul>",
    documentOwner: "Meeran Fehmi",
    contactEmail: "omar@manageaze.com",
    approvalAuthority: "Board Chair",
    approvalDate: minusDays(5),
    nextReviewDate: plusDays(90),
    versionHistory: [
      {
        id: "vh-3",
        torId: "tor-2",
        version: "v1.0",
        date: minusDays(10),
        updatedBy: "m-4",
        comments: "Initial approved version.",
      },
    ],
    approvalLog: [
      {
        id: "al-3",
        torId: "tor-2",
        userId: "m-3",
        action: "approved",
        createdAt: minusDays(6),
      },
      {
        id: "al-4",
        torId: "tor-2",
        userId: "m-1",
        action: "approved",
        createdAt: minusDays(5),
      },
    ],
    createdAt: minusDays(10),
    updatedAt: minusDays(5),
  },
  {
    id: "tor-3",
    frameworkId: "f-1",
    committeeId: "c-3",
    committeeOwnerId: "m-3",
    chairpersonId: "m-1",
    approverIds: ["m-1", "m-4"],
    reviewerIds: ["m-5"],
    memberIds: ["m-1", "m-3", "m-5"],
    minimumMembersRequired: 3,
    reportingWorkflow: "Risk register escalations are logged to board monthly review.",
    approvalWorkflow: "Chairperson plus one approver.",
    version: "v0.9",
    status: "draft",
    meetingFrequency: "monthly",
    purpose: "<p>Committee ensures risk and compliance controls are effective enterprise-wide.</p>",
    rolesAndResponsibilities: "<p>Pending final owner updates.</p>",
    agendaContent: "<p>Draft agenda pending.</p>",
    documentOwner: "Zoya Sifaat",
    contactEmail: "ayesha@manageaze.com",
    approvalAuthority: "Board Chair",
    approvalDate: null,
    nextReviewDate: plusDays(120),
    versionHistory: [],
    approvalLog: [],
    createdAt: minusDays(7),
    updatedAt: minusDays(2),
  },
  {
    id: "tor-4",
    frameworkId: "f-1",
    committeeId: "c-5",
    committeeOwnerId: "m-2",
    chairpersonId: "m-3",
    approverIds: ["m-3"],
    reviewerIds: ["m-5"],
    memberIds: ["m-2", "m-3", "m-5"],
    minimumMembersRequired: 3,
    reportingWorkflow: "Bi-weekly report to Risk & Compliance Committee.",
    approvalWorkflow: "Single approver flow.",
    version: "v0.3",
    status: "draft",
    meetingFrequency: "bi_weekly",
    purpose: "<p>Task force executes cyber resilience initiatives.</p>",
    rolesAndResponsibilities: "<p>Track incident-response drills and maturity actions.</p>",
    agendaContent: "<p>Agenda includes simulation reviews and patch governance.</p>",
    documentOwner: "Saad Hasnain",
    contactEmail: "saad@manageaze.com",
    approvalAuthority: "Chief Risk Officer",
    approvalDate: null,
    nextReviewDate: plusDays(60),
    versionHistory: [],
    approvalLog: [],
    createdAt: minusDays(4),
    updatedAt: minusDays(1),
  },
];

export const governanceMockMeetings: Meeting[] = [
  {
    id: "mtg-1",
    frameworkId: "f-1",
    committeeId: "c-1",
    title: "Board Q1 Governance Review",
    date: minusDays(7),
    attendees: ["m-1", "m-2", "m-3", "m-4", "m-6"],
    agendaItems: [
      "Approve annual governance reporting calendar",
      "Review audit remediation budget",
      "Evaluate committee attendance policy update",
    ],
    decisions: [
      "Budget increase for control remediation approved in principle",
      "Attendance policy revisions deferred to formal vote",
    ],
    actionItems: [
      {
        id: "act-1",
        description: "Submit revised attendance policy draft to board members",
        assigneeId: "m-2",
        dueDate: plusDays(3),
        status: "open",
      },
      {
        id: "act-2",
        description: "Circulate budget impact note with risk commentary",
        assigneeId: "m-4",
        dueDate: plusDays(2),
        status: "closed",
      },
    ],
    referencedTorIds: ["tor-1", "tor-2"],
    status: "published",
    minutesSentAt: minusDays(6),
  },
  {
    id: "mtg-2",
    frameworkId: "f-1",
    committeeId: "c-2",
    title: "Audit Committee - Control Findings Sync",
    date: minusDays(12),
    attendees: ["m-3", "m-4", "m-5", "m-6"],
    agendaItems: [
      "Review unresolved high severity control findings",
      "Confirm external audit readiness timeline",
    ],
    decisions: [
      "Three high-severity controls escalated to Board",
      "External audit prep plan approved",
    ],
    actionItems: [
      {
        id: "act-3",
        description: "Publish consolidated remediation tracker",
        assigneeId: "m-5",
        dueDate: minusDays(4),
        status: "closed",
      },
    ],
    referencedTorIds: ["tor-2"],
    status: "published",
    minutesSentAt: minusDays(11),
  },
  {
    id: "mtg-3",
    frameworkId: "f-1",
    committeeId: "c-2",
    title: "Audit Committee - Monthly Governance Session",
    date: minusDays(2),
    attendees: ["m-3", "m-4", "m-5"],
    agendaItems: [
      "Vote on extending quarterly audit deep-dives",
      "Discuss control dashboard rollout",
    ],
    decisions: ["Vote initiated for revised audit cadence; pending outcome."],
    actionItems: [
      {
        id: "act-4",
        description: "Prepare pilot scope for control dashboard",
        assigneeId: "m-5",
        dueDate: plusDays(6),
        status: "open",
      },
    ],
    referencedTorIds: ["tor-2"],
    status: "draft",
  },
  {
    id: "mtg-4",
    frameworkId: "f-1",
    committeeId: "c-1",
    title: "Board Special Session - Committee Effectiveness",
    date: minusDays(1),
    attendees: ["m-1", "m-2", "m-3", "m-4"],
    agendaItems: [
      "Review committee quorum consistency",
      "Decide on digital approval policy changes",
    ],
    decisions: ["Decision deferred until final voting window closes."],
    actionItems: [
      {
        id: "act-5",
        description: "Share draft digital approval SOP with all committee owners",
        assigneeId: "m-2",
        dueDate: plusDays(4),
        status: "open",
      },
    ],
    referencedTorIds: ["tor-1", "tor-3"],
    status: "draft",
  },
  {
    id: "mtg-5",
    frameworkId: "f-1",
    committeeId: "c-1",
    title: "Board Q4 Retrospective",
    date: minusDays(28),
    attendees: ["m-1", "m-2", "m-3", "m-4", "m-6"],
    agendaItems: ["Finalize governance operating model updates"],
    decisions: ["Operating model updates approved and published."],
    actionItems: [
      {
        id: "act-6",
        description: "Issue final governance handbook update",
        assigneeId: "m-2",
        dueDate: minusDays(20),
        status: "closed",
      },
    ],
    referencedTorIds: ["tor-1"],
    status: "published",
    minutesSentAt: minusDays(27),
  },
];

export const governanceMockVotingDecisions: VotingDecision[] = [
  {
    id: "vote-1",
    meetingId: "mtg-1",
    committeeId: "c-1",
    frameworkId: "f-1",
    title: "Approve Board Attendance Policy Revision",
    description: "Adopt revised attendance expectations and quorum escalation process.",
    proposedBy: "m-2",
    deadline: plusDays(5),
    status: "open",
    votes: [
      { memberId: "m-1", vote: "yes", castAt: minusDays(5) },
      { memberId: "m-3", vote: "abstain", castAt: minusDays(4) },
    ],
  },
  {
    id: "vote-2",
    meetingId: "mtg-2",
    committeeId: "c-2",
    frameworkId: "f-1",
    title: "Approve External Audit Readiness Plan",
    description: "Authorize the compliance tooling spend and readiness timeline.",
    proposedBy: "m-4",
    deadline: minusDays(8),
    status: "passed",
    votes: [
      { memberId: "m-3", vote: "yes", castAt: minusDays(10) },
      { memberId: "m-4", vote: "yes", castAt: minusDays(10) },
      { memberId: "m-5", vote: "yes", castAt: minusDays(9) },
    ],
  },
  {
    id: "vote-3",
    meetingId: "mtg-5",
    committeeId: "c-1",
    frameworkId: "f-1",
    title: "Approve Independent Governance Advisor",
    description: "Engage an external advisor for annual governance effectiveness review.",
    proposedBy: "m-1",
    deadline: minusDays(24),
    status: "rejected",
    votes: [
      { memberId: "m-1", vote: "yes", castAt: minusDays(25) },
      { memberId: "m-2", vote: "no", castAt: minusDays(25) },
      { memberId: "m-3", vote: "no", castAt: minusDays(24) },
      { memberId: "m-4", vote: "abstain", castAt: minusDays(24) },
    ],
  },
  {
    id: "vote-4",
    meetingId: "mtg-3",
    committeeId: "c-2",
    frameworkId: "f-1",
    title: "Extend Quarterly Audit Deep-Dive Sessions",
    description: "Move from one to two deep-dive sessions per quarter for priority controls.",
    proposedBy: "m-3",
    deadline: plusDays(9),
    status: "open",
    votes: [{ memberId: "m-4", vote: "yes", castAt: minusDays(1) }],
  },
];

export const governanceMockSnapshot: GovernanceSnapshot = {
  members: governanceMockMembers,
  frameworks: governanceMockFrameworks,
  committees: governanceMockCommittees,
  terms: governanceMockTerms,
  meetings: governanceMockMeetings,
  votingDecisions: governanceMockVotingDecisions,
};
